import { DeleteOutlined } from '@ant-design/icons';
import { ViewIcon } from '@chakra-ui/icons';
import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { FiEdit } from 'react-icons/fi';
import { HiOutlineDotsVertical } from 'react-icons/hi';

import {
  CaseClusterFieldsFragment,
  CaseClusterRelationClientsFragment,
  CaseClusterRelationMeetingsFragment,
} from '@/@generated/codegen/API';
import CaseClusterTag from '@/components/CaseClusterTag/CaseClusterTag';
import ClientTag from '@/components/ClientTag/ClientTag';
import { useConfirmModal } from '@/ui/ConfirmModal/ConfirmModal';
import dateInRangeFilterFn from '@/ui/TanstackTable/Filter/date/dateInRangeFilterFn';
import { parseDate } from '@/utils/helpers/parseDate';

export type AdminCaseClusterListItemType = CaseClusterFieldsFragment &
  CaseClusterRelationClientsFragment &
  CaseClusterRelationMeetingsFragment;

const columnHelper = createColumnHelper<AdminCaseClusterListItemType>();

type Args = {
  t: TFunction<'translation', undefined, 'translation'>;
  onEditClick: (data: AdminCaseClusterListItemType) => void;
  onDeleteClick: (caseClusterID: string) => void;
  confirmModalUtils: ReturnType<typeof useConfirmModal>;
};

const getColumns = ({ t, onDeleteClick, onEditClick, confirmModalUtils }: Args) => {
  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (props) => (
        <Tooltip label={props.getValue()}>
          <Text>{props.getValue()}</Text>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
      filterFn: dateInRangeFilterFn,
      cell: (props) => <Text>{parseDate(props.getValue().toString() || '')}</Text>,
    }),
    columnHelper.accessor('cases', {
      header: 'Cases Included',
      enableSorting: false,
      cell: (props) => {
        const cases = props.getValue().edges.map((edge) => edge.node);
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {cases.map((elem) => (
              <CaseClusterTag key={elem.id} name={elem.legacyCaseId} />
            ))}
          </Flex>
        );
      },
    }),
    columnHelper.accessor('meetings', {
      header: 'Linked Meetings',
      enableSorting: false,
      cell: (props) => {
        const elems = props.getValue().edges.map((el) => el.node);
        const { isOpen, onOpen, onClose } = useDisclosure();
        const firstElem = elems[0];

        return (
          <>
            <Flex flexDir="row" gap={2} align="center">
              {firstElem && (
                <Tooltip
                  label={
                    <div>
                      {firstElem.eventDate && <h2>{parseDate(firstElem.eventDate.toString())}</h2>}
                      {firstElem.name}
                    </div>
                  }
                >
                  <Tag colorScheme="blue" w="min">
                    <Text textOverflow="ellipsis" noOfLines={1}>
                      {firstElem.name}
                    </Text>
                  </Tag>
                </Tooltip>
              )}

              <Tooltip label="View all linked meetings">
                {elems.length > 1 && (
                  <IconButton
                    icon={<ViewIcon />}
                    aria-label="View all linked meetings"
                    size="sm"
                    onClick={onOpen}
                  />
                )}
              </Tooltip>
            </Flex>

            <Modal isOpen={isOpen} onClose={onClose} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Linked Meetings</ModalHeader>
                <ModalCloseButton />
                <ModalBody maxH="80vh" overflowY="auto" p={4}>
                  <Flex flexDir="column" gap={3} mb={4} justifyContent="center" alignItems="center">
                    {elems.map((elem) => (
                      <Tag key={elem.id} colorScheme="blue" w="fit-content">
                        <Tooltip
                          label={
                            <div>
                              {elem.eventDate && <h2>{parseDate(elem.eventDate.toString())}</h2>}
                              {elem.name}
                            </div>
                          }
                        >
                          <Text textOverflow="ellipsis" noOfLines={1}>
                            {elem.name}
                          </Text>
                        </Tooltip>
                      </Tag>
                    ))}
                  </Flex>
                </ModalBody>
              </ModalContent>
            </Modal>
          </>
        );
      },
    }),
    columnHelper.accessor('clients', {
      id: 'clients',
      header: 'Client',
      enableSorting: false,
      cell: (props) => {
        const clients = props.getValue().edges.map((edge) => edge.node);
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {clients.map((elem) => (
              <ClientTag key={elem.id} client={elem.name} />
            ))}
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 85,
      cell: (props) => {
        const data = props.row.original;
        return (
          <>
            <Menu closeOnSelect={false}>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<HiOutlineDotsVertical />}
                variant="outline"
                size="xs"
                border="none"
              />
              <MenuList>
                <MenuItem
                  icon={<FiEdit />}
                  onClick={() => {
                    onEditClick(data);
                  }}
                >
                  {t('common.edit')}
                </MenuItem>
                <MenuItem
                  icon={<DeleteOutlined />}
                  isDisabled={data.meetings.edges.length > 0}
                  onClick={() => {
                    confirmModalUtils.confirm({
                      title: (
                        <div>
                          {t('admin.caseClusters.delete.confirmText')}
                          <br />
                          {t('meeting.header.confirmRestart')}
                        </div>
                      ),
                      onConfirm: () => onDeleteClick(data.id),
                    });
                  }}
                >
                  {t('common.delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          </>
        );
      },
    }),
  ];

  return columns;
};

export default getColumns;
